import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiFileText, FiArrowRight } from "react-icons/fi";
import { FaTicketAlt } from "react-icons/fa";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import { NewsListSkeleton } from "../../../components/utils/Skeleton";
import NewsCard from "../../../components/portal/NewsCard";
import newsService from "../../../api/services/news";
import useReveal from "../../../hooks/useReveal";
import {
  formatNewsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
  newsTypes,
  slugify,
} from "../../../utils/utils";

import {
  Page,
  SectionLabel,
  PageH1,
  FiltersRow,
  ChipsRail,
  FilterChip,
  SearchWrap,
  SearchField,
  FeaturedCard,
  FeaturedImage,
  FeaturedBody,
  FeaturedMeta,
  TypeBadge,
  FeaturedDate,
  FeaturedOwner,
  FeaturedTitle,
  FeaturedExcerpt,
  FeaturedCtas,
  GhostCta,
  TicketCta,
  NewsGrid,
  RevealCell,
  LoadMoreRow,
  GhostButton,
  EmptyState,
} from "./NewsPageNew.styles";

const PAGE_SIZE = 12;
const MAX_FETCH_PAGES = 10;

/* Grid ćelija sa sopstvenim scroll-reveal observerom; --i je kolona u redu,
   pa red ulazi kaskadno sleva nadesno */
const RevealGridCell = ({ columnIndex, children }) => {
  const [ref, visible] = useReveal({ rootMargin: "0px 0px -40px 0px" });
  return (
    <RevealCell ref={ref} $visible={visible} style={{ "--i": columnIndex }}>
      {children}
    </RevealCell>
  );
};

export default function NewsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tipParam = searchParams.get("tip") || "";
  const q = searchParams.get("q") ?? searchParams.get("search") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);

  /* ------------------ sve novosti (backend paginira 12) ------------------ */
  const [allNews, setAllNews] = useState(null); // null = učitava se

  useEffect(() => {
    let active = true;

    const loadAll = async () => {
      try {
        const first = await newsService.getNews({ page: 1 });
        const firstItems = first?.data || [];
        const perPage = Number(first?.perPage) || PAGE_SIZE;
        const total = Number(first?.total) || firstItems.length;
        const totalPages = Math.min(
          Math.ceil(total / perPage) || 1,
          MAX_FETCH_PAGES,
        );

        let rest = [];
        if (totalPages > 1) {
          const responses = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              newsService.getNews({ page: i + 2 }),
            ),
          );
          rest = responses.flatMap((response) => response?.data || []);
        }

        if (!active) return;
        const seen = new Set();
        setAllNews(
          [...firstItems, ...rest].filter((item) => {
            if (!item?.id || seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          }),
        );
      } catch {
        if (active) setAllNews([]);
      }
    };

    loadAll();
    return () => {
      active = false;
    };
  }, []);

  /* ----------------------------- URL filteri ----------------------------- */
  const selectedType = useMemo(
    () =>
      newsTypes.find(
        (type) => type.value === tipParam || slugify(type.name) === tipParam,
      ) || null,
    [tipParam],
  );

  const applyParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    next.delete("search");
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    if (!("page" in patch)) next.delete("page");
    if (next.get("page") === "1") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== q) {
        applyParams({ q: searchInput.trim() || null });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  /* ------------------------------ filtriranje ------------------------------ */
  const filtered = useMemo(() => {
    if (!allNews) return [];
    const query = q.trim().toLowerCase();

    return allNews.filter((item) => {
      if (selectedType && item.type !== selectedType.value) return false;
      if (query) {
        const owner = item.band?.name || item.festival?.name || "";
        const haystack =
          `${item.title} ${owner} ${item.text || ""}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [allNews, selectedType, q]);

  const hasActiveFilters = Boolean(selectedType || q.trim());

  // Istaknuta novost, samo bez aktivnih filtera (featured ili najnovija)
  const featured = useMemo(() => {
    if (hasActiveFilters || filtered.length === 0) return null;
    return filtered.find((item) => item.isFeatured) || filtered[0];
  }, [filtered, hasActiveFilters]);

  const rest = useMemo(
    () =>
      featured ? filtered.filter((item) => item.id !== featured.id) : filtered,
    [filtered, featured],
  );

  const visible = rest.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < rest.length;
  const loading = allNews === null;

  const goTo = (path) => {
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  const FeaturedTypeIcon = featured ? getNewsTypeIcon(featured.type) : null;
  const featuredImage = featured
    ? featured.mainImage ||
      featured.newsImages?.find((img) => img.isMain)?.url ||
      featured.newsImages?.[0]?.url ||
      null
    : null;
  const featuredOwner = featured
    ? featured.band?.name || featured.festival?.name || ""
    : "";
  const featuredOwnerPath = featured
    ? featured.band?.id
      ? `/bend/${featured.band.slug || featured.band.id}`
      : featured.festival?.id
        ? `/festival/${featured.festival.slug || featured.festival.id}`
        : null
    : null;

  return (
    <MainContainer>
      <Seo title="Novosti" canonical="/novosti" />
      <Page>
        <SectionLabel>Novosti</SectionLabel>
        <PageH1>Novosti</PageH1>

        {/* Filteri, isti obrazac kao shop, u URL-u */}
        <FiltersRow>
          <ChipsRail>
            <FilterChip
              type="button"
              $active={!selectedType}
              onClick={() => applyParams({ tip: null })}
            >
              Sve
            </FilterChip>
            {newsTypes.map((type) => {
              const TypeIcon = type.icon;
              return (
                <FilterChip
                  key={type.value}
                  type="button"
                  $active={selectedType?.value === type.value}
                  onClick={() => applyParams({ tip: type.value })}
                >
                  <TypeIcon size={13} />
                  {type.name}
                </FilterChip>
              );
            })}
          </ChipsRail>

          <SearchWrap>
            <FiSearch size={15} />
            <SearchField
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Pretraži novosti..."
              aria-label="Pretraga novosti"
            />
          </SearchWrap>
        </FiltersRow>

        {loading ? (
          <NewsListSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState>
            <FiFileText size={40} />
            <p>Nema novosti za ovaj filter.</p>
            {hasActiveFilters && (
              <GhostButton
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearchParams({}, { replace: true });
                }}
              >
                Poništi filtere
              </GhostButton>
            )}
          </EmptyState>
        ) : (
          <>
            {/* Istaknuta novost, full-width, slika levo 50% */}
            {featured && (
              <FeaturedCard
                onClick={() => goTo(`/novost/${featured.slug || featured.id}`)}
              >
                <FeaturedImage>
                  {featuredImage && (
                    <img
                      src={featuredImage}
                      alt={featured.title}
                      decoding="async"
                      fetchpriority="high"
                    />
                  )}
                </FeaturedImage>
                <FeaturedBody>
                  <FeaturedMeta>
                    {featured.type && (
                      <TypeBadge>
                        <FeaturedTypeIcon size={12} />
                        {getNewsTypeLabel(featured.type)}
                      </TypeBadge>
                    )}
                    {featured.date && (
                      <FeaturedDate>
                        {formatNewsDate(featured.date)}
                      </FeaturedDate>
                    )}
                  </FeaturedMeta>

                  {featuredOwner && (
                    <FeaturedOwner
                      onClick={(e) => {
                        e.stopPropagation();
                        if (featuredOwnerPath) goTo(featuredOwnerPath);
                      }}
                    >
                      {featuredOwner}
                    </FeaturedOwner>
                  )}

                  <FeaturedTitle>{featured.title}</FeaturedTitle>
                  {featured.text && (
                    <FeaturedExcerpt>{featured.text}</FeaturedExcerpt>
                  )}

                  <FeaturedCtas>
                    <GhostCta
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        goTo(`/novost/${featured.slug || featured.id}`);
                      }}
                    >
                      Opširnije <FiArrowRight size={14} />
                    </GhostCta>
                    {featured.ticketLink && (
                      <TicketCta
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            featured.ticketLink,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <FaTicketAlt size={12} /> Karte
                      </TicketCta>
                    )}
                  </FeaturedCtas>
                </FeaturedBody>
              </FeaturedCard>
            )}

            {/* Uniformni grid ostalih, sve kartice iste visine */}
            {visible.length > 0 && (
              <NewsGrid>
                {visible.map((newsItem, index) => (
                  <RevealGridCell key={newsItem.id} columnIndex={index % 3}>
                    <NewsCard news={newsItem} />
                  </RevealGridCell>
                ))}
              </NewsGrid>
            )}

            {hasMore && (
              <LoadMoreRow>
                <GhostButton
                  type="button"
                  onClick={() => applyParams({ page: page + 1 })}
                >
                  Učitaj još ({rest.length - visible.length})
                </GhostButton>
              </LoadMoreRow>
            )}
          </>
        )}
      </Page>
    </MainContainer>
  );
}
